import { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Radio } from 'lucide-react';
import clsx from 'clsx';
import type { PlayByPlayEvent } from '../../types';

// ── Signal classification ─────────────────────────────────────────────────

export type SignalType = 'neutral' | 'positive' | 'negative' | 'signal' | 'high-signal';

const HIGH_SIGNAL_PATTERNS =
  /pitching (change|substitution)|relief pitcher|pitching substitution|ejection|injury|runner placed on base/i;
const SIGNAL_PATTERNS =
  /mound visit|wild pitch|passed ball|balk|defensive indifference|intent(ional)? walk/i;
const POSITIVE_PATTERNS =
  /^(single|double|triple|home run|walk|hit by pitch|sac(rifice)? fly|grand slam)/i;
const NEGATIVE_PATTERNS =
  /strikeout|double play|triple play|grounded into double play/i;

export function classifyEvent(play: PlayByPlayEvent): SignalType {
  const evt = play.event ?? '';

  if (HIGH_SIGNAL_PATTERNS.test(evt)) return 'high-signal';
  // Home run is always high-signal (critical scoring swing)
  if (/home run|grand slam/i.test(evt) && play.isScoringPlay) return 'high-signal';
  if (SIGNAL_PATTERNS.test(evt)) return 'signal';
  if (POSITIVE_PATTERNS.test(evt) || play.isScoringPlay) return 'positive';
  if (NEGATIVE_PATTERNS.test(evt)) return 'negative';
  return 'neutral';
}

// ── Visual helpers ────────────────────────────────────────────────────────

const SIGNAL_META: Record<
  SignalType,
  { icon: string; rowCls: string; labelCls: string; borderCls: string }
> = {
  'high-signal': {
    icon: '⚡',
    rowCls: 'bg-gold/8 border-l-2 border-gold/50',
    labelCls: 'text-gold font-bold',
    borderCls: '',
  },
  signal: {
    icon: '⚠',
    rowCls: 'bg-warn/5 border-l-2 border-warn/50',
    labelCls: 'text-warn font-semibold',
    borderCls: '',
  },
  positive: {
    icon: '🟢',
    rowCls: '',
    labelCls: 'text-win font-semibold',
    borderCls: '',
  },
  negative: {
    icon: '🔴',
    rowCls: '',
    labelCls: 'text-loss font-semibold',
    borderCls: '',
  },
  neutral: {
    icon: '·',
    rowCls: '',
    labelCls: 'text-gray-500',
    borderCls: '',
  },
};

const ORDINALS: Record<number, string> = { 1: '1st', 2: '2nd', 3: '3rd' };
function ordinal(n: number) {
  return ORDINALS[n] ?? `${n}th`;
}

function inningLabel(play: PlayByPlayEvent) {
  const half = play.halfInning === 'top' ? 'Top' : 'Bot';
  return `${ordinal(play.inning)} ${half}`;
}

// ── Component ─────────────────────────────────────────────────────────────

interface SignalFeedProps {
  plays: PlayByPlayEvent[];
  /** Max events to display in the rolling buffer (default 20) */
  maxEvents?: number;
  /** Game label shown in the header (e.g. "NYY @ BOS") */
  gameLabel?: string;
}

export function SignalFeed({ plays, maxEvents = 20, gameLabel }: SignalFeedProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);

  // Keep a stable key→play map so AnimatePresence can track new arrivals
  const displayPlays = plays.slice(0, maxEvents);

  // Auto-scroll to top (newest) whenever plays update and not paused
  useEffect(() => {
    if (!paused && scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [plays, paused]);

  if (displayPlays.length === 0) return null;

  return (
    <div className="card mt-2 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-4">
        <Radio className="w-3.5 h-3.5 text-win animate-pulse flex-shrink-0" />
        <span className="text-[11px] font-semibold text-gray-300 uppercase tracking-widest">
          Live Feed
        </span>
        {gameLabel && (
          <span className="text-[10px] text-gray-500 ml-auto">{gameLabel}</span>
        )}
      </div>

      {/* Feed */}
      <div
        ref={scrollRef}
        className="overflow-y-auto max-h-48 px-2 py-1.5 space-y-0.5"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <AnimatePresence initial={false}>
          {displayPlays.map((play, i) => {
            const type = classifyEvent(play);
            const meta = SIGNAL_META[type];
            const key = `${play.atBatIndex}-${play.inning}-${play.halfInning}-${i}`;
            return (
              <motion.div
                key={key}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={clsx(
                  'flex items-start gap-2 px-2 py-1.5 rounded text-xs',
                  meta.rowCls,
                  type === 'high-signal' && 'animate-pulse-once',
                )}
              >
                {/* Signal icon */}
                <span
                  className="flex-shrink-0 w-4 text-center leading-none mt-px"
                  aria-hidden="true"
                >
                  {meta.icon}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-1.5 flex-wrap">
                    <span className="text-[10px] text-gray-500 font-mono flex-shrink-0">
                      {inningLabel(play)}
                    </span>
                    {play.event && (
                      <span
                        className={clsx(
                          'text-[10px] uppercase tracking-wide',
                          meta.labelCls,
                        )}
                      >
                        {play.event}
                      </span>
                    )}
                    {!play.isComplete && (
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-win animate-pulse flex-shrink-0" />
                    )}
                  </div>
                  {play.description && (
                    <div className="text-[10px] text-gray-400 leading-snug line-clamp-1 mt-0.5">
                      {play.description}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
  );
}
